// Personal PTO page: balance + new request + history.
// Sep 8 mobile rebuild: same page on phone and desktop — three stats, a
// request card with thumb-sized controls, requests as cards. The submit
// is pinned above the tab bar on the phone.

import { useEffect, useState } from 'react';
import { api } from '../../api';
import PinGate from '../../components/PinGate';
import { useIsMobile, MobilePage, StickyAction, Icon } from '../../mobile';

function days(start, end) {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return Math.max(0, Math.round(ms / 86400000) + 1);
}
const TYPE_LABEL = { vacation: 'Vacation', sick: 'Sick', personal: 'Personal' };
const STATUS = {
  pending: { bg: '#fffbeb', fg: '#b45309' }, approved: { bg: '#f0fdf4', fg: '#15803d' },
  rejected: { bg: '#fef2f2', fg: '#dc2626' }, denied: { bg: '#fef2f2', fg: '#dc2626' },
};
const inputStyle = { width: '100%', fontSize: 16, minHeight: 48, borderRadius: 12 };

function PtoContent({ activeUser }) {
  const isMobile = useIsMobile();
  const userId = activeUser?.id;
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ request_type: 'vacation', start_date: '', end_date: '', hours_requested: 8 });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  function load() {
    if (!userId) return;
    api.ptoBalance(userId).then(setBalance).catch(() => setBalance(null));
    api.listPtoRequests().then(setRequests).catch(() => setRequests([]));
  }
  useEffect(load, [userId]);

  const ready = form.start_date && form.end_date && form.hours_requested > 0;
  async function submit() {
    setErr(''); setDone(false);
    if (!ready) { setErr('Pick the dates and hours first.'); return; }
    setBusy(true);
    try {
      await api.createPtoRequest(form);
      setForm({ request_type: 'vacation', start_date: '', end_date: '', hours_requested: 8 });
      setDone(true); load();
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const submitBtn = (
    <button type="button" className="m-btn m-btn-primary" onClick={submit} disabled={busy || !ready}>
      <Icon name="check" size={20} color="#fff" /> {busy ? 'Submitting…' : 'Submit PTO request'}
    </button>
  );
  const body = (
    <>
      {balance && (
        <div className="m-stats">
          <div className="m-stat" style={{ '--m-stat-color': '#3b82f6' }}><div className="m-stat-value">{(balance.balance_hours || 0).toFixed(1)}h</div><div className="m-stat-label">Balance</div></div>
          <div className="m-stat" style={{ '--m-stat-color': '#64748b' }}><div className="m-stat-value">{(balance.accrual_hours_per_period || 0).toFixed(1)}h</div><div className="m-stat-label">Per period</div></div>
          <div className="m-stat" style={{ '--m-stat-color': '#d97706' }}><div className="m-stat-value">{(balance.used_hours || 0).toFixed(1)}h</div><div className="m-stat-label">Used</div></div>
        </div>
      )}

      <div className="m-section">New request</div>
      <div className="m-card" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
        {err && <div className="alert alert-error" style={{ margin: 0 }}>{err}</div>}
        {done && <div style={{ padding: '10px 12px', borderRadius: 10, background: '#f0fdf4', color: '#15803d', fontWeight: 600, fontSize: 14 }}>Request sent to your manager.</div>}
        <div>
          <label className="sales-label">Type</label>
          <div className="m-chips" style={{ marginBottom: 0 }}>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <button key={k} type="button" className={`m-chip ${form.request_type === k ? 'active' : ''}`} onClick={() => setForm(f => ({ ...f, request_type: k }))}>{v}</button>
            ))}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label className="sales-label">First day</label><input className="sales-input" type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} /></div>
          <div><label className="sales-label">Last day</label><input className="sales-input" type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'end' }}>
          <div><label className="sales-label">Hours</label><input className="sales-input" type="number" inputMode="decimal" min="0" step="0.5" value={form.hours_requested} onChange={e => setForm(f => ({ ...f, hours_requested: parseFloat(e.target.value) || 0 }))} style={inputStyle} /></div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', paddingBottom: 14 }}>{days(form.start_date, form.end_date)} day{days(form.start_date, form.end_date) === 1 ? '' : 's'}</div>
        </div>
        {!isMobile && submitBtn}
      </div>

      <div className="m-section">My requests{requests.length ? ` · ${requests.length}` : ''}</div>
      {requests.length === 0 ? (
        <div className="m-card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No requests yet.</div>
      ) : (
        <div className="m-list">
          {requests.map(r => {
            const s = STATUS[r.status] || { bg: 'var(--bg-inset)', fg: 'var(--text-secondary)' };
            return (
              <div key={r.id} className="m-card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{TYPE_LABEL[r.request_type] || r.request_type} · {r.hours_requested}h</div>
                  <span className="m-pill" style={{ background: s.bg, color: s.fg }}>{String(r.status).toUpperCase()}</span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{r.start_date === r.end_date ? r.start_date : `${r.start_date} → ${r.end_date}`}</div>
                {r.manager_notes && <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>“{r.manager_notes}”</div>}
              </div>
            );
          })}
        </div>
      )}
      {isMobile && <StickyAction>{submitBtn}</StickyAction>}
    </>
  );

  if (isMobile) return <MobilePage title="PTO" subtitle={activeUser?.full_name}>{body}</MobilePage>;
  return (
    <div className="page" style={{ maxWidth: 640 }}>
      <div className="page-header"><h2>PTO</h2></div>
      {body}
    </div>
  );
}

export default function PtoRequestPage() {
  return (
    <PinGate title="PTO">
      {(activeUser) => <PtoContent activeUser={activeUser} />}
    </PinGate>
  );
}
